import React, { useEffect, useState } from "react";
import {
  ActionIcon,
  ActionTable,
  DurationSummary,
  InteractionSummary,
  StyledContainer,
  StyledHeader,
} from "../Encounters/Encounters.styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Icon,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box
} from "@mui/material";
import SimpleGrid, { BodyProps } from "components/shared/SimpleGrid/SimpleGrid";
import { useTranslation } from "react-i18next";
import {
  ActionsInterface,
  EncounterInterface,
  EntryInterface
} from "context/initialState/Encounter";
import API from "api";
import Session from "helpers/session";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import { useParams } from "react-router-dom";
import moment from "moment";
import { associateReadingType } from "helpers/variables";

export default function Encounters() {
  const { memberId } = useParams();
  const { t } = useTranslation();
  // const TableHeaders: CellProps[] = [ {content: t('u-starts-at'), align: 'left'}, {content: t('u-all'), align: 'left'}, {content: 'Note', align: 'left'}, {content: t('u-actor'), align: 'left'} ];
  const [totalEncounterList, setTotalEncounterList] = useState<number>(0);
  const [allEncounters, setAllEncounters] = useState<EncounterInterface[]>([]);
  const [membersEncounters, setMembersEncounters] = useState<
    EncounterInterface[] | undefined
  >([]);

  const getAllCoordinatorEncounters = async (
    memberId: string
  ): Promise<EncounterInterface[]> => {
    return API.kore.encounters.getByMember(
      Session.crackCookieToken("coordinator"),
      memberId
    );
  };

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    const limit = itemsPerPage ?? 10;
    const page = currentPage ?? 0;
    const start = page * limit;
    setMembersEncounters(allEncounters.slice(start, start + limit));
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "message":
        return "mail_outline";
      case "note":
        return "sticky_note_2";
      // case "readings review":
      //     return "add_chart_outline";
      case "profile change":
        return "manage_accounts_outline";
      case "intervention":
        return "interpreter_mode_outline";
      case "readings review":
        return "grading_outline";
      default:
        return "question_outline";
    }
  };

  const formatDuration = (duration: string | number): string => {
    const totalSeconds = Number(duration);

    if (!duration || totalSeconds === 0) return "N/A";
    if (isNaN(totalSeconds)) return "N/A";

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n: number) => String(n).padStart(2, "0");

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  const getActionDisplayText = (action: ActionsInterface): React.ReactNode => {
    if (action.type === "note" && action.noteContent) {
      return `${action.noteContent}`;
    } else if (action.type === "intervention") {
      switch (action.noteContent) {
        case "Patient viewed":
          return "Patient Viewed";
        case "Patient closed":
          return "Patient Closed";
        default:
          return "Breach Response";
      }
    } else if (action.type === "message") {
      return `Message: ${action.noteContent || ""}`;
    } else if (action.type === "readings review") {
      if(action.noteContent === "Data exported") {
        return `${action.noteContent || ""}`;
      } else {
        const [readingType, readingId] = action.noteContent?.split(",") || ["", ""];
        return (
          <div>
            Readings Review:
            <div style={{ paddingLeft: "16px" }}>
              <div>Reading Type: {associateReadingType(readingType)}</div>
              <div>Reading Id: {readingId}</div>
            </div>
          </div>
        );
      }
    } else if (action.type === "profile change") {
      return `${action.noteContent || ""}`;
    }
    return "";
  };

  const renderTouchpoints = (tp: EntryInterface[]) => {
    return tp.map((item, idx1) => {
      return (
        <div key={idx1}>
          <DurationSummary>
            <span>Duration: {formatDuration(item.totalDuration)}</span>
            {/*Until escalated duration logic is solid this will be removed from the visual*/}
            {/*<span>*/}
            {/*  Escalated duration: {formatDuration(item.escalatedDuration)}*/}
            {/*</span>*/}
          </DurationSummary>
          <ActionTable>
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell>
                <TableCell>Interaction</TableCell>
                <TableCell>Coordinator</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.interactions.map((interaction, idx2) => {
                return interaction.actions.map((action, idx3) => {
                  return (
                    <TableRow key={idx3}>
                      <TableCell sx={{ width: "80px" }}>
                        {moment(action.timestamp).format("HH:mm:ss")}
                      </TableCell>
                      <TableCell sx={{ width: "140px" }}>
                        <ActionIcon title={action.type}>
                          {getActionIcon(action.type)}
                        </ActionIcon>{" "}
                        {action.type}
                      </TableCell>
                      <TableCell sx={{ width: "200px" }}>
                        {interaction.coordinatorName}
                      </TableCell>
                      <TableCell sx={{ width: "300px" }}>
                        {getActionDisplayText(action)}
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </ActionTable>
        </div>
      );
    });
  };

  const getInteractionCount = (touchpoints: EntryInterface[]) => {
    return touchpoints.flatMap((touchpoint) =>
      touchpoint.interactions.flatMap((interaction) => interaction.actions)
    ).length;
  };

  const generateList = (rawList: EncounterInterface[]): BodyProps[] => {
    return rawList.map((item, idx) => {
      const interactionCount = getInteractionCount(item.touchPoints);

      return {
        cells: [
          {
            content: (
              <Accordion key={idx}>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <b>{moment(item.day).format("ddd, MMM DD")}</b>
                  <InteractionSummary>
                    {interactionCount} {interactionCount === 1 ? "interaction" : "interactions"}
                  </InteractionSummary>
                </AccordionSummary>
                <AccordionDetails>
                  {renderTouchpoints(item.touchPoints)}
                </AccordionDetails>
              </Accordion>
            )
          }
        ]
      };
    });
  };

  useEffect(() => {
    window.scroll({
      top: 172,
      left: 0,
      behavior: "smooth"
    });

    getAllCoordinatorEncounters(memberId || "").then((_encounters) => {
      setAllEncounters(_encounters);
      setTotalEncounterList(_encounters.length);
      setMembersEncounters(_encounters.slice(0, 10));
    });
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 175,
        bottom: 75,
        left: 300,
        right: 115,
        overflow: "auto"
      }}
    >
      <StyledContainer>
      <EncounterTimerDisplay/>
        <Grid container>
          <Grid item md={1.5} lg={1} />
          <Grid item md={10.5} lg={11}>
            <StyledHeader>
              <h2>{t("u-encounters")}</h2>
            </StyledHeader>
            <SimpleGrid
              body={generateList(membersEncounters || [])}
              paginationAction={paginationAction}
              totalList={totalEncounterList}
            />
          </Grid>
        </Grid>
      </StyledContainer>
    </Box>
  );
}
